import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { urlMedia } from "@/lib/apercu";
import { toast } from "sonner";
import { Plane, ImagePlus, X, Loader2, Plus, ShieldCheck, Video } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AIR_BENGUE, TYPES_AIR, PAYS_BENGUE } from "@/lib/airBengue";
import { getUser, formatFCFA } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Champ = ({ label, children, aide }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500">{label}</span>
    <span className="mt-2 block">{children}</span>
    {aide && <span className="mt-1 block text-xs text-stone-400">{aide}</span>}
  </label>
);

const classeInput =
  "w-full rounded-2xl border border-[#E5DFD5] bg-[#FAF8F5] px-4 py-3 text-sm font-semibold text-stone-800 outline-none focus:border-stone-900";

export default function DeposerAirBengue() {
  const user = getUser();
  const navigate = useNavigate();
  const [tarifs, setTarifs] = useState(null);
  const [palier, setPalier] = useState("semaine");
  const [form, setForm] = useState({
    titre: "", type_prestation: "massage", ville: "", pays: "Belgique", tarif: "",
    disponible: true, texte: "", nom_annonceur: user?.firstname || "",
  });
  const [telephones, setTelephones] = useState([""]);
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    axios.get(`${API}/annonces/tarifs`).then((r) => setTarifs(r.data.air)).catch(() => {});
  }, []);

  const offre = tarifs?.[palier];
  const maj = (champ) => (e) =>
    setForm({ ...form, [champ]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onPhotos = async (e) => {
    const fichiers = Array.from(e.target.files || []);
    if (!fichiers.length || !offre) return;
    if (photos.length + fichiers.length > offre.photosMax) {
      toast.error(`Maximum ${offre.photosMax} photo(s) pour cette formule.`);
      return;
    }
    setChargement(true);
    try {
      for (const f of fichiers) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/annonces/upload`, fd);
        setPhotos((prev) => [...prev, res.data]);
      }
    } catch {
      toast.error("Échec de l'envoi d'une photo.");
    } finally {
      setChargement(false);
      e.target.value = "";
    }
  };
  /** Vidéo de présentation (formules Semaine et Mois) : 30 s maximum. */
  const onVideo = async (e) => {
    const fichier = e.target.files?.[0];
    if (!fichier || !offre) return;
    setChargement(true);
    try {
      const duree = await new Promise((resolve) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => resolve(v.duration);
        v.onerror = () => resolve(0);
        v.src = URL.createObjectURL(fichier);
      });
      if (offre.videoDureeSecondesMax && duree > offre.videoDureeSecondesMax) {
        toast.error(`La vidéo ne doit pas dépasser ${offre.videoDureeSecondesMax} secondes.`);
        return;
      }
      const fd = new FormData();
      fd.append("file", fichier);
      const res = await axios.post(`${API}/annonces/upload`, fd);
      setVideo({ ...res.data, duree_secondes: duree });
    } catch {
      toast.error("Échec de l'envoi de la vidéo.");
    } finally {
      setChargement(false);
      e.target.value = "";
    }
  };

  const publier = async () => {
    // Annonceurs à l'étranger : on garde le « + » et l'indicatif pays du numéro.
    const numeros = telephones.map((t) => t.replace(/[^\d+]/g, "")).filter(Boolean);
    if (!form.titre.trim() || !form.ville.trim()) {
      toast.error("Titre et ville sont obligatoires.");
      return;
    }
    if (!form.nom_annonceur.trim()) {
      toast.error("Indiquez le nom à afficher sur l'annonce.");
      return;
    }
    if (!numeros.length || numeros.some((n) => n.replace(/\D/g, "").length < 8)) {
      toast.error("Numéro de contact invalide", {
        description: "Saisissez un numéro joignable (8 chiffres minimum, WhatsApp de préférence).",
      });
      return;
    }
    if (!photos.length) {
      toast.error("Ajoutez au moins une photo.");
      return;
    }
    setEnvoi(true);
    try {
      const res = await axios.post(`${API}/annonces/deposer`, {
        profile_id: user.id, categorie: "air", palier,
        texte: form.texte, nom_annonceur: form.nom_annonceur.trim(), telephones: numeros,
        photos,
        video: offre?.videoMax ? video : null,
        air: {
          titre: form.titre.trim(), type_prestation: form.type_prestation,
          ville: form.ville.trim(), pays: form.pays,
          tarif: form.tarif ? Number(form.tarif) : null, disponible: form.disponible,
        },
      });
      if (res.data.payment_url) {
        localStorage.setItem("fodindin_transaction", res.data.transaction_id);
        window.location.href = res.data.payment_url;
        return;
      }
      toast.success("Annonce enregistrée", {
        description: "Elle sera visible dès validation par notre équipe.",
      });
      navigate(AIR_BENGUE.route);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Publication impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF8F5]">
        <Navbar />
        <main className="mx-auto max-w-2xl px-4 pb-24 pt-32 text-center">
          <h1 className="font-heading text-2xl font-bold text-stone-900">Connectez-vous d'abord</h1>
          <Link to="/connexion" className="mt-6 inline-flex rounded-full bg-[#E05638] px-6 py-3 text-sm font-bold text-white">
            Me connecter
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-6" data-testid="air-deposer-page">
        <span
          className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em]"
          style={{ background: AIR_BENGUE.colorSoft, color: AIR_BENGUE.color }}
        >
          <Plane size={13} className="-rotate-45" /> {AIR_BENGUE.name}
        </span>
        <h1 className="font-heading mt-5 text-4xl font-bold tracking-tight text-stone-900 sm:text-5xl">
          Publier une annonce Bengué
        </h1>
        <p className="mt-3 text-base text-stone-600">
          Vous êtes installé(e) hors de Côte d'Ivoire ? Présentez votre prestation à toute la
          communauté fodindinca. Chaque annonce est vérifiée avant publication.
        </p>

        {/* formules */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="air-paliers">
          {tarifs &&
            Object.entries(tarifs)
              .filter(([id]) => id !== "gratuit")
              .map(([id, o]) => {
                const actif = palier === id;
                return (
                  <button
                    key={id}
                    onClick={() => setPalier(id)}
                    data-testid={`air-palier-${id}`}
                    aria-pressed={actif}
                    className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                      actif
                        ? "border-[#E05638] bg-white shadow-lg"
                        : "border-[#E5DFD5] bg-[#FAF8F5] hover:border-stone-400"
                    }`}
                  >
                    <span className={`block text-sm font-bold capitalize ${actif ? "text-stone-900" : "text-stone-700"}`}>
                      {id === "jour" ? "24 heures" : id}
                    </span>
                    <span className="mt-1 block text-lg font-bold" style={{ color: AIR_BENGUE.color }}>
                      {formatFCFA(o.prix)}
                    </span>
                    <span className={`mt-1 block text-xs ${actif ? "text-stone-500" : "text-stone-500"}`}>
                      {o.photosMax} photos · {o.dureeJours} jours
                    </span>
                  </button>
                );
              })}
        </div>

        <div className="mt-8 space-y-5 rounded-3xl border border-[#E5DFD5] bg-white p-6">
          <Champ label="Titre de l'annonce">
            <input
              value={form.titre}
              onChange={maj("titre")}
              maxLength={80}
              placeholder="Ex. Massage relaxant à Bruxelles"
              data-testid="air-titre-input"
              className={classeInput}
            />
          </Champ>

          <div className="grid gap-5 sm:grid-cols-2">
            <Champ label="Type de prestation">
              <select
                value={form.type_prestation}
                onChange={maj("type_prestation")}
                data-testid="air-type-select"
                className={classeInput}
              >
                {TYPES_AIR.filter((t) => t.id !== "tous").map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </Champ>
            <Champ label="Tarif indicatif (FCFA)" aide="Laissez vide pour « tarif sur demande ».">
              <input
                value={form.tarif}
                onChange={(e) => setForm({ ...form, tarif: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                inputMode="numeric"
                placeholder="45000"
                data-testid="air-tarif-input"
                className={classeInput}
              />
            </Champ>
            <Champ label="Ville">
              <input
                value={form.ville}
                onChange={maj("ville")}
                placeholder="Bruxelles"
                data-testid="air-ville-input"
                className={classeInput}
              />
            </Champ>
            <Champ label="Pays">
              <select value={form.pays} onChange={maj("pays")} data-testid="air-pays-select" className={classeInput}>
                {PAYS_BENGUE.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Champ>
          </div>

          <Champ label="Nom affiché">
            <input
              value={form.nom_annonceur}
              onChange={maj("nom_annonceur")}
              maxLength={40}
              data-testid="air-nom-input"
              className={classeInput}
            />
          </Champ>

          <Champ label="Numéros à contacter" aide="Visibles uniquement par les abonnés Air-Bengue.">
            <span className="block space-y-2">
              {telephones.map((num, i) => (
                <span key={i} className="flex items-center gap-2">
                  <input
                    value={num}
                    onChange={(e) =>
                      setTelephones(telephones.map((t, idx) =>
                        idx === i ? e.target.value.replace(/[^\d+]/g, "").slice(0, 15) : t))}
                    placeholder="+32470123456"
                    data-testid={`air-telephone-input-${i}`}
                    className={classeInput}
                  />
                  {telephones.length > 1 && (
                    <button
                      onClick={() => setTelephones(telephones.filter((_, idx) => idx !== i))}
                      data-testid={`air-telephone-supprimer-${i}`}
                      aria-label="Retirer ce numéro"
                      className="rounded-full border border-[#E5DFD5] p-2.5 text-stone-400 hover:border-red-300 hover:text-red-600"
                    >
                      <X size={15} />
                    </button>
                  )}
                </span>
              ))}
            </span>
            {telephones.length < 4 && (
              <button
                onClick={() => setTelephones([...telephones, ""])}
                data-testid="air-telephone-ajouter"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-4 py-2 text-xs font-bold text-stone-700 hover:border-stone-900"
              >
                <Plus size={13} /> Ajouter un numéro
              </button>
            )}
          </Champ>

          <Champ label="Description">
            <textarea
              value={form.texte}
              onChange={maj("texte")}
              rows={6}
              maxLength={1000}
              placeholder="Décrivez votre prestation, vos disponibilités, votre cadre…"
              data-testid="air-description-input"
              className={`${classeInput} resize-none`}
            />
            <div className="text-right text-[11px] text-stone-400" data-testid="air-description-compteur">
              {(form.texte || "").length}/1000 caractères
            </div>
          </Champ>

          <label className="flex items-center gap-2 text-sm font-semibold text-stone-700">
            <input
              type="checkbox"
              checked={form.disponible}
              onChange={maj("disponible")}
              data-testid="air-disponible-checkbox"
              className="h-4 w-4 accent-[#4C6EF5]"
            />
            Je suis actuellement disponible
          </label>

          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500">
              Photos {offre ? `(max ${offre.photosMax})` : ""}
            </span>
            <div className="mt-3 flex flex-wrap gap-3">
              {photos.map((p, i) => (
                <span key={p.hash} className="relative">
                  <img src={urlMedia(p.url)} alt="" className="h-20 w-20 rounded-xl object-cover" />
                  <button
                    onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                    data-testid={`air-photo-supprimer-${i}`}
                    aria-label="Retirer la photo"
                    className="absolute -right-2 -top-2 rounded-full bg-stone-900 p-1 text-white"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <label
                className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-xl border-2 border-dashed border-[#E5DFD5] text-stone-400 hover:border-stone-900"
                data-testid="air-photo-ajouter"
              >
                {chargement ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                <input type="file" accept="image/*" multiple onChange={onPhotos} className="hidden" />
              </label>
            </div>
          </div>

          {offre?.videoMax > 0 && (
            <div data-testid="air-section-video">
              <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500">
                Vidéo de présentation (≤{offre.videoDureeSecondesMax}s)
              </span>
              <div className="mt-3 flex items-center gap-3">
                {video ? (
                  <>
                    <video
                      src={urlMedia(video.url)}
                      controls
                      data-testid="air-video-apercu"
                      className="h-28 rounded-xl border border-[#E5DFD5] bg-black"
                    />
                    <button
                      onClick={() => setVideo(null)}
                      data-testid="air-video-supprimer"
                      aria-label="Retirer la vidéo"
                      className="rounded-full bg-stone-900 p-1.5 text-white transition-colors duration-200 hover:bg-red-600"
                    >
                      <X size={12} />
                    </button>
                  </>
                ) : (
                  <label
                    className="flex cursor-pointer items-center gap-2 rounded-2xl border-2 border-dashed border-[#E5DFD5] px-4 py-3 text-sm font-semibold text-stone-500 transition-colors duration-200 hover:border-stone-900"
                    data-testid="air-video-ajouter"
                  >
                    {chargement ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}
                    Ajouter une vidéo de {offre.videoDureeSecondesMax} secondes
                    <input type="file" accept="video/*" onChange={onVideo} className="hidden" />
                  </label>
                )}
              </div>
            </div>
          )}

          <p className="flex items-start gap-2 rounded-2xl bg-[#FAF8F5] p-4 text-xs leading-relaxed text-stone-500">
            <ShieldCheck size={15} className="mt-0.5 shrink-0" style={{ color: AIR_BENGUE.color }} />
            Vos photos sont hébergées sur nos serveurs, vos numéros ne sont jamais publics et chaque
            annonce est relue par notre équipe avant publication.
          </p>

          <button
            onClick={publier}
            disabled={envoi || chargement}
            data-testid="air-publier-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-stone-900 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-stone-700 disabled:opacity-60"
          >
            {envoi ? <Loader2 size={15} className="animate-spin" /> : <Plane size={15} className="-rotate-45" />}
            {offre ? `Publier et payer ${formatFCFA(offre.prix)}` : "Publier mon annonce"}
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
